import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowDownRight } from 'lucide-react';
import { EditableText, EditableImage, useEditMode } from '../../components';

function PageHeroBlock({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const { editMode } = useEditMode();
  const ref = useRef(null);
  const bg = data.background_image?.src;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, []);

  const title = data.title || '';
  const titleParts = typeof title === 'string' ? title.split(':') : [title];

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pt-36 pb-24 sm:pt-44 sm:pb-32 bg-olive-footer"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <EditableImage
          src={bg}
          alt={data.background_image?.alt || ''}
          onChange={(src, alt) => update({ background_image: { src, alt } })}
          className="absolute inset-0 w-full h-full pointer-events-auto"
          imgProps={{ className: 'w-full h-full object-cover' }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(120deg, rgba(46,58,24,0.86), rgba(92,64,48,0.62))' }}
        />
      </div>
      <div className="absolute inset-0 bg-grid-olive [background-size:42px_42px] opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_75%)] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: 'radial-gradient(600px circle at var(--mx,50%) var(--my,30%), rgba(197,120,99,0.22), transparent 50%)' }} />
      <motion.div animate={{ x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }} className="absolute -top-32 -right-24 w-[460px] h-[460px] rounded-full bg-accent/25 blur-3xl pointer-events-none" />
      <motion.div animate={{ x: [0, -30, 0], y: [0, 20, 0] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }} className="absolute -bottom-32 -left-24 w-[460px] h-[460px] rounded-full bg-olive-neon/30 blur-3xl pointer-events-none" />
      <div className="absolute top-[88px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />

      <div className="container-page relative">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-white/90 text-[10px] uppercase tracking-[0.3em] mb-6">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-75" />
              <span className="relative rounded-full h-2 w-2 bg-accent" />
            </span>
            <Sparkles size={11} className="text-accent" />
            <EditableText
              value={data.badge_text || 'TTSS Experience'}
              onChange={(t) => update({ badge_text: t })}
              as="span"
            />
          </div>

          {(data.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {data.tags.map((t, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full glass text-white text-xs font-medium tracking-wide hover:border-accent/60 hover:text-accent transition-all">
                  <EditableText
                    value={t}
                    onChange={(v) => {
                      const next = [...data.tags];
                      next[i] = v;
                      update({ tags: next });
                    }}
                    as="span"
                  />
                </span>
              ))}
            </div>
          )}

          <h1 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight">
            {editMode ? (
              <EditableText value={title} onChange={(t) => update({ title: t })} as="span" />
            ) : (
              titleParts.map((part, i, arr) =>
                i === arr.length - 1 && arr.length > 1 ? (
                  <span key={i} className="block">
                    <span className="text-gradient">{part}</span>
                  </span>
                ) : (
                  <span key={i}>{part}{arr.length > 1 && i < arr.length - 1 ? ':' : ''}<br /></span>
                )
              )
            )}
          </h1>

          {data.description && (
            <p className="mt-6 text-white/85 text-lg leading-relaxed max-w-2xl">
              <EditableText value={data.description} onChange={(t) => update({ description: t })} as="span" multiline />
            </p>
          )}

          <div className="mt-10 inline-flex items-center gap-5 px-5 py-3 rounded-2xl glass">
            <div className="flex items-center gap-2 text-xs text-white/80 uppercase tracking-[0.18em]">
              <ArrowDownRight size={14} className="text-accent animate-bounce" />
              <EditableText
                value={data.scroll_label || 'Scroll to explore'}
                onChange={(t) => update({ scroll_label: t })}
                as="span"
              />
            </div>
            <div className="h-4 w-px bg-white/20" />
            <EditableText
              value={data.established_text || 'EST · 1965'}
              onChange={(t) => update({ established_text: t })}
              as="div"
              className="text-[10px] text-white/60 uppercase tracking-[0.22em] font-mono"
            />
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/70 to-transparent pointer-events-none" />
    </section>
  );
}

export default PageHeroBlock;
